'use client';

import { useLocale } from 'next-intl';
import { useRouter, usePathname } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { ChevronDown, Loader2 } from 'lucide-react';
import { useState, useEffect, useRef, useTransition, useCallback } from 'react';
import { FlagIcon, FlagIconCode } from 'react-flag-kit';
import { useTranslations } from 'next-intl';

// Define the language interface
interface Language {
  code: string;
  name: string;
  nativeName: string;
  countryCode: FlagIconCode;
}

const languages: Language[] = [
  // EU Languages
  {
    code: 'bg',
    name: 'Bulgarian',
    nativeName: 'Български',
    countryCode: 'BG' as FlagIconCode
  },
  {
    code: 'hr',
    name: 'Croatian',
    nativeName: 'Hrvatski',
    countryCode: 'HR' as FlagIconCode
  },
  {
    code: 'cs',
    name: 'Czech',
    nativeName: 'Čeština',
    countryCode: 'CZ' as FlagIconCode
  },
  {
    code: 'da',
    name: 'Danish',
    nativeName: 'Dansk',
    countryCode: 'DK' as FlagIconCode
  },
  {
    code: 'nl',
    name: 'Dutch',
    nativeName: 'Nederlands',
    countryCode: 'NL' as FlagIconCode
  },
  {
    code: 'en',
    name: 'English',
    nativeName: 'English',
    countryCode: 'EU' as FlagIconCode
  },
  {
    code: 'et',
    name: 'Estonian',
    nativeName: 'Eesti',
    countryCode: 'EE' as FlagIconCode
  },
  {
    code: 'fi',
    name: 'Finnish',
    nativeName: 'Suomi',
    countryCode: 'FI' as FlagIconCode
  },
  {
    code: 'fr',
    name: 'French',
    nativeName: 'Français',
    countryCode: 'FR' as FlagIconCode
  },
  {
    code: 'de',
    name: 'German',
    nativeName: 'Deutsch',
    countryCode: 'DE' as FlagIconCode
  },
  {
    code: 'el',
    name: 'Greek',
    nativeName: 'Ελληνικά',
    countryCode: 'GR' as FlagIconCode
  },
  {
    code: 'hu',
    name: 'Hungarian',
    nativeName: 'Magyar',
    countryCode: 'HU' as FlagIconCode
  },
  {
    code: 'is',
    name: 'Icelandic',
    nativeName: 'Íslenska',
    countryCode: 'IS' as FlagIconCode
  },
  {
    code: 'ga',
    name: 'Irish',
    nativeName: 'Gaeilge',
    countryCode: 'IE' as FlagIconCode
  },
  {
    code: 'it',
    name: 'Italian',
    nativeName: 'Italiano',
    countryCode: 'IT' as FlagIconCode
  },
  {
    code: 'lv',
    name: 'Latvian',
    nativeName: 'Latviešu',
    countryCode: 'LV' as FlagIconCode
  },
  {
    code: 'lt',
    name: 'Lithuanian',
    nativeName: 'Lietuvių',
    countryCode: 'LT' as FlagIconCode
  },
  {
    code: 'mt',
    name: 'Maltese',
    nativeName: 'Malti',
    countryCode: 'MT' as FlagIconCode
  },
  {
    code: 'pl',
    name: 'Polish',
    nativeName: 'Polski',
    countryCode: 'PL' as FlagIconCode
  },
  {
    code: 'pt',
    name: 'Portuguese',
    nativeName: 'Português',
    countryCode: 'PT' as FlagIconCode
  },
  {
    code: 'ro',
    name: 'Romanian',
    nativeName: 'Română',
    countryCode: 'RO' as FlagIconCode
  },
  {
    code: 'sk',
    name: 'Slovak',
    nativeName: 'Slovenčina',
    countryCode: 'SK' as FlagIconCode
  },
  {
    code: 'sl',
    name: 'Slovenian',
    nativeName: 'Slovenščina',
    countryCode: 'SI' as FlagIconCode
  },
  {
    code: 'es',
    name: 'Spanish',
    nativeName: 'Español',
    countryCode: 'ES' as FlagIconCode
  },
  {
    code: 'sv',
    name: 'Swedish',
    nativeName: 'Svenska',
    countryCode: 'SE' as FlagIconCode
  },
  // Non-EU Languages
  {
    code: 'zh-CN',
    name: '简体中文',
    nativeName: '中文',
    countryCode: 'CN' as FlagIconCode
  }
];

export function LanguageSwitcher() {
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);
  const [isPending, startTransition] = useTransition();
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const t = useTranslations('LanguageSwitcher');

  const switchLanguage = (newLocale: string) => {
    if (newLocale === locale) {
      setIsOpen(false);
      return;
    }

    setIsOpen(false);

    // Remove the current locale from the pathname
    const pathWithoutLocale = pathname.replace(`/${locale}`, '') || '/';

    // Navigate to the new locale using startTransition to prevent flash
    const newPath = `/${newLocale}${pathWithoutLocale}`;

    startTransition(() => {
      router.push(newPath);
    });
  };

  // Keyboard search functionality
  const handleKeyPress = useCallback((event: KeyboardEvent) => {
    if (!isOpen) return;

    const key = event.key.toLowerCase();

    // Handle escape key
    if (key === 'escape') {
      setIsOpen(false);
      setHighlightedIndex(-1);
      return;
    }

    // Handle enter key
    if (key === 'enter' && highlightedIndex >= 0) {
      switchLanguage(languages[highlightedIndex].code);
      return;
    }

    // Handle arrow keys
    if (key === 'arrowdown') {
      event.preventDefault();
      setHighlightedIndex(prev =>
        prev < languages.length - 1 ? prev + 1 : 0
      );
      return;
    }

    if (key === 'arrowup') {
      event.preventDefault();
      setHighlightedIndex(prev =>
        prev > 0 ? prev - 1 : languages.length - 1
      );
      return;
    }

    // Handle letter keys for search
    if (key.length === 1 && /[a-z]/.test(key)) {
      event.preventDefault();

      // Find the first language that starts with the pressed key
      const matchingIndex = languages.findIndex(lang =>
        lang.name.toLowerCase().startsWith(key) ||
        lang.nativeName.toLowerCase().startsWith(key)
      );

      if (matchingIndex !== -1) {
        setHighlightedIndex(matchingIndex);

        // Scroll the highlighted item into view
        setTimeout(() => {
          const highlightedElement = document.querySelector(`[data-language-index="${matchingIndex}"]`);
          if (highlightedElement) {
            highlightedElement.scrollIntoView({
              block: 'nearest',
              behavior: 'smooth'
            });
          }
        }, 0);
      }
    }
  }, [isOpen, highlightedIndex, switchLanguage]);

  const currentLanguage = languages.find(lang => lang.code === locale) || languages.find(lang => lang.code === 'en')!;

  // Close dropdown when clicking outside and handle keyboard events
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyPress);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [isOpen, handleKeyPress]);

  // Reset highlighted index when dropdown opens
  useEffect(() => {
    if (isOpen) {
      // Set initial highlight to current language
      const currentIndex = languages.findIndex(lang => lang.code === locale);
      setHighlightedIndex(currentIndex >= 0 ? currentIndex : -1);
    } else {
      setHighlightedIndex(-1);
    }
  }, [isOpen, locale]);

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        disabled={isPending}
        className="flex items-center gap-2 h-9 px-3 bg-card hover:bg-accent hover:text-accent-foreground border border-border shadow-sm transition-colors"
      >
        {isPending ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <div className="w-5 h-4 overflow-hidden flex items-center justify-center">
            <FlagIcon code={currentLanguage.countryCode} size={16} />
          </div>
        )}
        <span className="hidden sm:inline text-sm font-medium">
          {isPending ? t('switching') : currentLanguage.nativeName}
        </span>
        <span className="sm:hidden text-xs font-medium">
          {isPending ? '...' : currentLanguage.code.toUpperCase()}
        </span>
        {!isPending && (
          <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
        )}
      </Button>

      {isOpen && !isPending && (
        <div className="absolute right-0 top-full mt-2 bg-popover border border-border rounded-md shadow-lg z-50 min-w-[160px] max-h-[400px] overflow-y-auto">
          <div className="py-1">
            {languages.map((language, index) => {
              const isSelected = locale === language.code;
              const isHighlighted = index === highlightedIndex;
              return (
                <button
                  key={language.code}
                  data-language-index={index}
                  onClick={() => switchLanguage(language.code)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  className={`w-full text-left px-3 py-2.5 flex items-center gap-3 transition-colors ${isSelected
                    ? 'bg-accent text-accent-foreground font-medium'
                    : isHighlighted
                      ? 'bg-accent/50 text-accent-foreground'
                      : 'text-popover-foreground hover:bg-accent hover:text-accent-foreground'
                    }`}
                >
                  <div className="w-5 h-4 overflow-hidden flex items-center justify-center">
                    <FlagIcon code={language.countryCode} size={16} />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{language.nativeName}</span>
                    <span className="text-xs text-muted-foreground">{t('languages.' + language.code)}</span>
                  </div>
                  {isSelected && (
                    <div className="ml-auto">
                      <div className="w-2 h-2 bg-primary rounded-full"></div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}